import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useApplyFirmTaskTemplateMutation,
  useGetFirmTaskTemplatesQuery,
  usePreviewApplyFirmTaskTemplateMutation,
} from '@/services/api/firmTaskTemplatesService';
import { MatterListItem } from '@/services/types/matter-types';
import { getApiErrorMessage } from '@/utils/api-error';

interface ApplyTaskTemplateModalProps {
  open: boolean;
  onClose: () => void;
  selectedMatters: MatterListItem[];
  onSuccess?: () => void;
}

type Step = 'select' | 'preview' | 'result';

interface PreviewData {
  total_matters: number;
  will_create: number;
  already_exists: number;
}

interface ApplyResultData {
  workflow_id: string;
  total_matters: number;
}

export const ApplyTaskTemplateModal = ({
  open,
  onClose,
  selectedMatters,
  onSuccess,
}: ApplyTaskTemplateModalProps) => {
  const [step, setStep] = useState<Step>('select');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultData, setResultData] = useState<ApplyResultData | null>(null);

  // Group matters by firm
  const mattersByFirm = useMemo(() => {
    return selectedMatters.reduce((grouped, matter) => {
      const { id } = matter.firm;
      const existing = grouped.get(id);
      if (existing) {
        existing.matters.push(matter);
      } else {
        grouped.set(id, { firmName: matter.firm.name, matters: [matter] });
      }
      return grouped;
    }, new Map<string, { firmName: string; matters: MatterListItem[] }>());
  }, [selectedMatters]);

  // Check if all matters are from the same firm
  const singleFirm = mattersByFirm.size === 1;
  const firmId = singleFirm ? Array.from(mattersByFirm.keys())[0] : null;
  const firmName = singleFirm
    ? Array.from(mattersByFirm.values())[0].firmName
    : null;

  // Fetch templates for the firm (only if single firm)
  const { data: templates, isLoading: isLoadingTemplates } =
    useGetFirmTaskTemplatesQuery(firmId ?? '', {
      skip: !firmId || !open,
    });

  // Filter to only applicable templates (auto_create or manual_only, not archived)
  const applicableTemplates = useMemo(
    () =>
      templates?.filter(
        (t) => t.status === 'auto_create' || t.status === 'manual_only',
      ) ?? [],
    [templates],
  );

  const selectedTemplate = useMemo(
    () => applicableTemplates.find((t) => t.id === selectedTemplateId),
    [applicableTemplates, selectedTemplateId],
  );

  // API mutations
  const [previewApply, { isLoading: isPreviewing }] =
    usePreviewApplyFirmTaskTemplateMutation();

  const [applyTemplate, { isLoading: isApplying }] =
    useApplyFirmTaskTemplateMutation();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep('select');
      setSelectedTemplateId('');
      setPreviewData(null);
      setError(null);
      setResultData(null);
    }
  }, [open]);

  const handlePreview = async () => {
    if (!firmId || !selectedTemplateId) return;

    try {
      const result = await previewApply({
        firmId,
        templateId: selectedTemplateId,
        data: {
          matter_ids: selectedMatters.map((m) => m.id),
        },
      }).unwrap();

      setPreviewData(result);
      setStep('preview');
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to preview. Please try again.'));
    }
  };

  const handleApply = async () => {
    if (!firmId || !selectedTemplateId) return;

    try {
      const result = await applyTemplate({
        firmId,
        templateId: selectedTemplateId,
        data: {
          matter_ids: selectedMatters.map((m) => m.id),
        },
      }).unwrap();

      setResultData({
        workflow_id: result.workflow_id,
        total_matters: result.total_matters,
      });
      setStep('result');
      setError(null);
      toast.success('Task template applied successfully');
      onSuccess?.();
    } catch (err) {
      setError(
        getApiErrorMessage(err, 'Failed to apply template. Please try again.'),
      );
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply Task Template</DialogTitle>
          <DialogDescription>
            Create tasks from a template for the selected matters.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Multi-firm warning - only show on select step */}
        {!singleFirm && step === 'select' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Selected matters belong to {mattersByFirm.size} different firms.
              Task templates can only be applied to matters from a single firm.
              Please filter your selection to one firm.
            </AlertDescription>
          </Alert>
        )}

        {/* Step: Select Template */}
        {step === 'select' && singleFirm && (
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-zinc-50 dark:bg-zinc-900 p-3">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                <span className="font-medium">{selectedMatters.length}</span>{' '}
                matters selected from{' '}
                <span className="font-medium">{firmName}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="template">Task Template</Label>
              {isLoadingTemplates && (
                <div className="text-sm text-zinc-500">
                  Loading templates...
                </div>
              )}
              {!isLoadingTemplates && applicableTemplates.length === 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No task templates found for this firm. Create templates in
                    the firm&apos;s Scope of Work settings.
                  </AlertDescription>
                </Alert>
              )}
              {!isLoadingTemplates && applicableTemplates.length > 0 && (
                <Select
                  value={selectedTemplateId}
                  onValueChange={setSelectedTemplateId}
                >
                  <SelectTrigger id="template">
                    <SelectValue placeholder="Select a task template" />
                  </SelectTrigger>
                  <SelectContent>
                    {applicableTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selectedTemplate && (
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3 space-y-2">
                <p className="font-medium text-sm">{selectedTemplate.name}</p>
                {selectedTemplate.description && (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {selectedTemplate.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500 dark:text-zinc-400">
                  <span>Type: {selectedTemplate.task_type}</span>
                  {selectedTemplate.assignee_role && (
                    <span>Assignee: {selectedTemplate.assignee_role}</span>
                  )}
                  {selectedTemplate.sla_resolution_policy && (
                    <span>SLA: {selectedTemplate.sla_resolution_policy}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && previewData && (
          <div className="space-y-4 py-4">
            <Alert>
              <AlertDescription>
                Review the preview below. Click &quot;Apply&quot; to create the
                tasks.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-zinc-100 dark:bg-zinc-800 p-4 text-center">
                <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                  {previewData.total_matters}
                </p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                  Total matters
                </p>
              </div>
              <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-4 text-center">
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                  {previewData.will_create}
                </p>
                <p className="text-sm text-green-600 dark:text-green-500">
                  Will create
                </p>
              </div>
              <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/30 p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                  {previewData.already_exists}
                </p>
                <p className="text-sm text-yellow-600 dark:text-yellow-500">
                  Already exists
                </p>
              </div>
            </div>

            {previewData.already_exists > 0 && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {previewData.already_exists} matter(s) already have this task
                and will be skipped.
              </p>
            )}
          </div>
        )}

        {/* Step: Result */}
        {step === 'result' && resultData && (
          <div className="space-y-4 py-4">
            <Alert className="bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                Task template applied successfully! Tasks are being created for{' '}
                {resultData.total_matters} matters.
              </AlertDescription>
            </Alert>

            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              The tasks will be created in the background. You can close this
              dialog and continue working.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 'select' && (
            <>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                disabled={
                  !singleFirm ||
                  !selectedTemplateId ||
                  isPreviewing ||
                  isLoadingTemplates
                }
              >
                {isPreviewing ? 'Loading...' : 'Preview'}
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('select')}>
                Back
              </Button>
              <Button
                onClick={handleApply}
                disabled={isApplying || previewData?.will_create === 0}
              >
                {isApplying ? 'Applying...' : 'Apply'}
              </Button>
            </>
          )}

          {step === 'result' && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
